import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { Skill } from '../types';

export const findUserMediaSkill: Skill = {
    name: SKILL_NAMES.findUserMedia,
    description:
        "Working with the user's own media — attachments in the chat and their uploaded library. Load BEFORE inventorying or searching the user's uploads and attachments.",
    kind: 'howto',
    surfaces: ['sketch'],
    get instructions(): string {
        return `# Finding and Using the User's Own Media

The user's own material beats stock — it's authentic, on-brand, and specific to
their story in a way stock never is. Inventory it before searching the library, and
use every suitable piece.

## Where it lives
- **Attached to the chat**: files appear as \`### MEDIA … ID: <id> … ###\`
  blocks. Media the user included in their request often *is* the video's visuals —
  treat it as first priority.
- **Their uploads**: \`${TOOL_NAMES.searchMedia}\` with \`source: "uploads"\`.
  \`${TOOL_NAMES.listAvailableMedia}\` only lists media already gathered in this conversation
  (attachments, source-document media, earlier search results) — uploads you haven't searched for
  won't be in it. Many users keep their uploads in folders; browsing without a query lists
  them. When the user names a folder, pass it as \`folder\` on every uploads search and use only
  media from it — folder contents often have meaningless file names, so browse the folder (paging
  with \`page\`) rather than searching it by name.
  When the preferences say previous uploads aren't auto-suggested, search them only when the user
  points you to their own files.
- A document's images count too — a PDF's images are the user's material and take
  priority over stock where they fit.

## What "suitable" means
Genuinely usable as scene media: relevant to the scene's point,
and not a logo or chart fragment that would look broken filling a media frame.
Being the user's own doesn't make a broken-looking crop acceptable.

## Understanding a clip before using it
When you need to know what a user's video actually contains — content, pacing,
visual style — call \`${TOOL_NAMES.analyzeVideo}\` with its media id; it returns the
transcript plus a shot-by-shot tile strip. Use it before building scenes around
footage you haven't seen.

## Budget media slots when you pick designs
Every media slot in a design (multi-media and collage designs have several) needs its own
piece of media. Before adding scenes, count the slots in the designs you plan to use against the
suitable media you have. If you're short, close the gap in the plan, not with repeats: pick
designs with fewer slots, fill the gap with stock when it's enabled, or re-plan the scenes around
the media you actually have.

## Reuse
Reusing the user's own media across scenes is fine when the point requires it
(showing the same product twice). Don't stretch one asset across every scene just
because it's there — cover the remaining scenes from their other material or stock.
Never repeat media on consecutive scenes: its animation visibly restarts.`;
    },
};
